import { Label, Link, Persona, PersonaSize, Stack, TextField } from '@fluentui/react';
import { Patient, PatientPreferredDoctor } from '../../../api';
import { Address } from '../../../api/accounts.types';
import { GENDER_TYPE, PATIENT_LANGUAGES, PCP_TYPE } from '../../../config/constants';
import { ROUTES } from '../../../config/routes';
import { AsyncData } from '../../../redux/core';
import { formatPhoneNumber } from '../../../util/PhoneNumber';
import { ManagingAgencies } from '../../account/ManagingAgencies';
import { Clipboard } from '../../core/Clipboard';
import { HubBadge } from '../../core/HubBadge';
import { PreferredDoctorPersona } from '../../core/PreferredDoctorPersona';
import { Tile } from '../../core/Tile';
import { AttributionHistoryLink } from '../AttributionHistory';
import { EditPreferredDoctor } from '../EditPreferredDoctor';
import { InsuranceAdditionalBenefits } from '../InsuranceAdditionalBenefits';
import { PatientFlags } from '../PatientFlags';
import { PatientNotes } from '../PatientNotes';
import { SecondaryContact } from '../SecondaryContact';
import ReferralSourcePicker from './ReferralSourcePicker';

interface Props {
  patient?: Patient;
  preferredDoctorState: AsyncData<PatientPreferredDoctor>;
  onSecondaryContactUpdated: (patient: Patient) => void;
  onPatientUpdated: (patientId: string) => void;
  homeAddress?: Address;
}
export const PatientProfileTab = ({ patient, preferredDoctorState, onPatientUpdated, onSecondaryContactUpdated, homeAddress }: Props) => {
  if (!patient) {
    return null;
  }

  const gender = GENDER_TYPE.find((item) => item.key === patient.genderId);
  const formattedPhone = formatPhoneNumber(patient.phone);

  return (
    <Stack root={{ style: { marginTop: 10 } }} tokens={{ childrenGap: 12 }}>
      <Stack horizontal>
        <Persona
          size={PersonaSize.size100}
          imageUrl={patient.avatarUrl}
          text={patient.firstName + ' ' + patient.lastName}
          secondaryText={patient.email}
          tertiaryText={formattedPhone}
          data-tid="txt_patientNameEmailPhone"
        />
        <Tile text="Completed" value={patient.completedVisits} />
        <Tile text="Canceled" value={patient.cancelledVisits} />
        {patient.isHubActive && <HubBadge />}
      </Stack>

      <Stack horizontal>
        <TextField label="Date of Birth" value={patient.dateOfBirth || 'N/A'} borderless readOnly />
        <TextField label="Sex" value={gender ? gender.text : 'N/A'} borderless readOnly />
        <TextField label="Preferred Language" value={PATIENT_LANGUAGES[patient.preferredLanguage]} borderless readOnly />
      </Stack>
      <Stack tokens={{ childrenGap: 36 }} horizontal>
        <SecondaryContact patient={patient} onUpdated={onSecondaryContactUpdated} />
        <Clipboard className="cursor-copy" content={patient.mrn} message="MRN has been copied to clipboard">
          <TextField label="MRN" value={patient.mrn} styles={{ field: { ':hover': { cursor: 'copy' } } }} readOnly borderless />
        </Clipboard>
      </Stack>
      <Stack>
        <TextField label="Home Address" value={homeAddress ? formattedAddress(homeAddress!!) : 'None'} borderless readOnly />
      </Stack>
      <Stack>
        <EditPreferredDoctor label="Preferred Provider" patient={patient} preferredDoctor={preferredDoctorState} onSave={onPatientUpdated} />
        <PreferredDoctorPersona preferredDoctor={preferredDoctorState} linkToProfile />
      </Stack>

      <Stack>
        <Label>Additional Insurance Plan Benefits</Label>
        <InsuranceAdditionalBenefits patient={patient} />
      </Stack>

      <Stack>
        <TextField label="Attribution status" value={PCP_TYPE.find((status) => status.key === preferredDoctorState.data?.status)?.text || 'No Attribution'} borderless readOnly />
        <AttributionHistoryLink patientId={patient.patientId} />
      </Stack>

      <Stack>
        <Label>Managed by Partner(s)</Label>
        <ManagingAgencies userAccountId={patient.ownerAccountId} readonly />
        <Link href={`${ROUTES.accounts}/${patient.ownerAccountId}`}>Update partners on account page...</Link>
      </Stack>

      <PatientFlags patientId={patient.patientId} label="Flags" />
      <Stack>
        <Label>Referral Source</Label>
        <ReferralSourcePicker patientId={patient.patientId} />
      </Stack>

      <div style={{ minWidth: 600, maxWidth: 800 }}>
        <PatientNotes patientId={patient.patientId} />
      </div>
    </Stack>
  );
};

const formattedAddress = (address: Pick<Address, 'streetAddress' | 'unit' | 'city' | 'state' | 'zipcode' | 'country'>) => {
  const currentAddressValues = [address.streetAddress, address.city, address.state, address.zipcode, address.country];

  // If any of the fields required to assemble the address is undefined or empty (''), return nothing.
  if (currentAddressValues.some((v) => v === undefined || v === '')) return '';

  var result = address.streetAddress;
  if (address.unit.length !== 0) {
    result += `, ${address.unit}`;
  }
  result += `, ${address.city}, ${address.state} ${address.zipcode}, ${address.country}`;
  return result;
};
